import { AdminMfaPrompt } from '@/features/auth/security-page';
import { Link } from '@tanstack/react-router';
import { FileQuestion, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/page-header';
import { Button, EmptyState, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui';
import { ShareDialog } from '@/components/share-dialog';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

import { RANGE_OPTIONS, type RangeDays } from './admin-schemas';
import { AuditSection } from './audit-section';
import { BusinessesSection } from './businesses-section';
import { ContentSection } from './content-section';
import { CreateBusinessDialog } from './create-business-dialog';
import { OverviewSection } from './overview-section';
import { ReportsSection } from './reports-section';
import { UsageSection } from './usage-section';
import { UsersSection } from './users-section';

/**
 * /admin — קונסולת מפעיל הפלטפורמה. מי שאינו מפעיל רואה "לא נמצא" (לא "אין
 * הרשאה": אין סיבה לאשר שהדף קיים). זו הסתרת UI בלבד — השרת מחזיר 403 לכל
 * קריאה של מי שאינו מפעיל.
 */
export function AdminPage() {
  const { session, status } = useAuth();
  if (status === 'loading' || !session) return null;
  if (!session.isPlatformAdmin) return <AdminNotFound />;
  // אימות דו-שלבי חובה למפעילים — בלעדיו השרת מחזיר 403.
  if (!session.mfaEnabled) return <AdminMfaPrompt />;
  return <AdminConsole />;
}

function AdminNotFound() {
  const { t } = useTranslation('admin');
  return (
    <EmptyState
      icon={FileQuestion}
      title={t('notFound.title')}
      description={t('notFound.description')}
      action={
        <Link to="/" className="text-sm font-medium text-accent hover:underline">
          {t('notFound.back')}
        </Link>
      }
    />
  );
}

type Tab = 'overview' | 'usage' | 'businesses' | 'users' | 'reports' | 'content' | 'audit';

function AdminConsole() {
  const { t } = useTranslation('admin');
  const [days, setDays] = useState<RangeDays>(30);
  const [tab, setTab] = useState<Tab>('overview');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<{
    name: string;
    result: { url: string; waUrl: string | null; message: string };
  } | null>(null);
  // טווח 7/30/90 שייך לסקירה, לשימוש ולעסקים. לדוחות יש טווח תאריכים משלהם.
  const usesRange = tab === 'overview' || tab === 'usage' || tab === 'businesses';

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        action={
          <div className="flex flex-wrap items-center gap-2">
            {usesRange ? <RangePicker value={days} onChange={setDays} /> : null}
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus aria-hidden />
              {t('create.button')}
            </Button>
          </div>
        }
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <div className="-mx-1 overflow-x-auto px-1">
          <TabsList>
            <TabsTrigger value="overview">{t('tabs.overview')}</TabsTrigger>
            <TabsTrigger value="usage">{t('tabs.usage')}</TabsTrigger>
            <TabsTrigger value="businesses">{t('tabs.businesses')}</TabsTrigger>
            <TabsTrigger value="users">{t('tabs.users')}</TabsTrigger>
            <TabsTrigger value="reports">{t('tabs.reports')}</TabsTrigger>
            <TabsTrigger value="content">{t('tabs.content')}</TabsTrigger>
            <TabsTrigger value="audit">{t('tabs.audit')}</TabsTrigger>
          </TabsList>
        </div>
        {/* רק הלשונית הפתוחה נטענת — כל קריאה נרשמת ביומן הביקורת. */}
        <TabsContent value="overview" className="pt-5">
          {tab === 'overview' ? <OverviewSection days={days} /> : null}
        </TabsContent>
        <TabsContent value="usage" className="pt-5">
          {tab === 'usage' ? <UsageSection days={days} /> : null}
        </TabsContent>
        <TabsContent value="businesses" className="pt-5">
          {tab === 'businesses' ? <BusinessesSection days={days} /> : null}
        </TabsContent>
        <TabsContent value="users" className="pt-5">
          {tab === 'users' ? <UsersSection /> : null}
        </TabsContent>
        <TabsContent value="reports" className="pt-5">
          {tab === 'reports' ? <ReportsSection /> : null}
        </TabsContent>
        <TabsContent value="content" className="pt-5">
          {tab === 'content' ? <ContentSection /> : null}
        </TabsContent>
        <TabsContent value="audit" className="pt-5">
          {tab === 'audit' ? <AuditSection /> : null}
        </TabsContent>
      </Tabs>

      {creating ? (
        <CreateBusinessDialog
          onClose={() => setCreating(false)}
          onCreated={(r) => {
            setCreating(false);
            setCreated({
              name: r.business.name,
              result: { url: r.loginUrl, waUrl: r.waUrl, message: r.message },
            });
          }}
        />
      ) : null}
      {created ? (
        <ShareDialog
          result={created.result}
          title={t('create.createdTitle', { name: created.name })}
          description={t('create.createdDescription')}
          onClose={() => setCreated(null)}
        />
      ) : null}
    </div>
  );
}

function RangePicker({ value, onChange }: { value: RangeDays; onChange: (d: RangeDays) => void }) {
  const { t } = useTranslation('admin');
  return (
    <div
      role="group"
      aria-label={t('page.rangeLabel')}
      className="inline-flex rounded-(--radius-md) border border-border bg-surface p-0.5 shadow-xs"
    >
      {RANGE_OPTIONS.map((d) => (
        <button
          key={d}
          type="button"
          aria-pressed={value === d}
          onClick={() => onChange(d)}
          className={cn(
            'min-h-9 rounded-(--radius-sm) px-3 text-xs font-medium transition-colors duration-(--duration-fast)',
            value === d
              ? 'bg-accent text-fg-on-accent shadow-xs'
              : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
          )}
        >
          {t('page.rangeOption', { days: d })}
        </button>
      ))}
    </div>
  );
}
